// 3rd Party
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

// Components
import { Div, FlexRow, H2 } from 'common/styles/oc-styled-components';
import { colors } from 'common/styles/style-constants';

import { selectDataProfileLink } from '../../../../connect/selectors';
import InsuranceBillSvg from '../../../common/components/svgs/InsuranceBillSvg';
import InsuranceFinancingSvg from '../../../common/components/svgs/InsuranceFinancingSvg';
import {
  BlueCrossSvg,
  CanadaLifeSvg,
  DesjardinsSvg,
  GreenShieldSvg,
  ManulifeHorizontalSvg,
  SunLifeHorizontalSvg,
} from '../../../common/components/svgs/InsuranceSvgs';
import InsuranceUmbrellaSvg from '../../../common/components/svgs/InsuranceUmbrellaSvg';

// Other
import { ONTARIO_INSURANCES } from '../../../common/constants/search-constants';
import { FEATURES } from '../../constants/constants';
import { isOntarioDentistProvider } from '../../helpers/profile-helpers';
import { getInsuranceName } from '../../helpers/profile-need-to-know-helpers';

import { Description, ItemsList } from './styled-components';

const getInsuranceIcon = (provider) => {
  switch (provider) {
    case ONTARIO_INSURANCES.blueCross:
      return <BlueCrossSvg />;
    case ONTARIO_INSURANCES.desjardins:
      return <DesjardinsSvg />;
    case ONTARIO_INSURANCES.canadaLife:
      return <CanadaLifeSvg />;
    case ONTARIO_INSURANCES.greenShield:
      return <GreenShieldSvg />;
    case ONTARIO_INSURANCES.manulife:
      return <ManulifeHorizontalSvg />;
    case ONTARIO_INSURANCES.sunlife:
      return <SunLifeHorizontalSvg />;
    default:
      return undefined;
  }
};

const InsurancePaymentNeedToKnow = ({ clinic, hasInsurance, insurance }) => {
  const directLink = useSelector(selectDataProfileLink);
  const { acceptsDentalInsuranceOnAssignment } = clinic.metadata;
  const clinicOffersFinancing = _.find(clinic.features, {
    id: FEATURES.financing,
  });
  const isInNetwork = _.includes(
    clinic.insurancePlans,
    _.get(insurance, 'planId'),
  );
  const patientCouldHaveInsurance =
    hasInsurance !== 'No' && !_.get(insurance, 'socialAssistance');
  const { providers } = clinic;
  const showInNetworkInfo =
    _.get(insurance, 'planId') || (_.get(insurance, 'type') && !directLink);
  const showOntarioInsuranceInsight =
    patientCouldHaveInsurance
    && _.some(providers, (provider) =>
      isOntarioDentistProvider(
        clinic.address,
        _.get(provider, 'doctor.specialties'),
      ));
  const showOntarioInsuranceProvider =
    _.get(insurance, 'topProvider') && !directLink;

  return (
    <ItemsList>
      {showOntarioInsuranceInsight
      && (
        <FlexRow as="li">
          <Div width="40px">
            <InsuranceUmbrellaSvg color={colors.sage} width="40px" />
          </Div>
          <Div margin="0 0 0 30px">
            {showOntarioInsuranceProvider
              ? (
                <>
                  <H2 display="inline" margin="0 20px 0 0" verticalAlign="middle">
                    Accepts your insurance
                  </H2>
                  {getInsuranceIcon(insurance.provider)}
                </>
              )

              : <H2>Accepts most insurance plans</H2>}
          </Div>
        </FlexRow>
      )}
      {showInNetworkInfo
      && (
        <FlexRow as="li">
          <Div width="40px">
            <InsuranceUmbrellaSvg color={colors.sage} width="40px" />
          </Div>
          <Div margin="0 0 0 30px">
            <H2>{isInNetwork ? 'In-Network' : 'Out-of-Network'}</H2>
            <Description>{getInsuranceName(insurance)}</Description>
          </Div>
        </FlexRow>
      )}
      {patientCouldHaveInsurance
      && (
        <FlexRow margin="20px 0 0 0" as="li">
          <Div width="40px">
            <InsuranceBillSvg color={colors.sage} width="38px" />
          </Div>
          <Div margin="0 0 0 30px">
            {acceptsDentalInsuranceOnAssignment
              ? (
                <>
                  <H2>Bills your insurance directly</H2>
                  <Description>
                    You will only pay for costs not covered by your plan
                  </Description>
                </>
              )

              : (
                <>
                  <H2>Pay upfront, get reimbursed later</H2>
                  <Description>
                    Settle your payments with the practice upfront, and get
                    reimbursed by your insurance company after submitting a claim
                  </Description>
                </>
              )}
          </Div>
        </FlexRow>
      )}
      {clinicOffersFinancing
      && (
        <FlexRow margin="20px 0 0 0" as="li">
          <Div width="40px">
            <InsuranceFinancingSvg color={colors.sage} width="40px" />
          </Div>
          <Div margin="0 0 0 30px">
            <H2>Financing Plans Available</H2>
            <Description>
              This office offers flexible low-interest financing options to
              ensure all patients can get the dental care they need
            </Description>
          </Div>
        </FlexRow>
      )}
    </ItemsList>
  );
};

InsurancePaymentNeedToKnow.propTypes = {
  clinic: PropTypes.object,
  insurance: PropTypes.object,
  hasInsurance: PropTypes.string,
};

export default InsurancePaymentNeedToKnow;
